import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AppProps } from 'next/app';
import { setCookie } from 'nookies';
import { Provider as ReakitProvider } from 'reakit';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import urljoin from 'url-join';
import { IntercomProvider } from 'react-use-intercom';
import { Organization } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import Prism from 'prism-react-renderer/prism';

import DefaultScripts from '@/components/DefaultScripts';
import { ApplicationProvider } from '@/contexts/application';
import { NavigationProvider } from '@/contexts/navigation';
import { cookieParams } from '@/utils/cookie';
import {
  __PROD__,
  INTERCOM_APP_ID,
  INTERCOM_INITIALIZE_DELAY,
} from '@/utils/constants';
import * as gtm from '@/lib/gtm';

import { defaultSeo, defaultSeoUrl } from '../../next-seo.config';

import 'tailwindcss/tailwind.css';
import '@/styles/global.css';
import '@/styles/lib/nprogress.css';
import '@/styles/lib/docsearch.css';

(typeof global !== `undefined` ? global : window).Prism = Prism;

interface MyAppProps extends AppProps {
  err: any;
}

export default function MyApp({ Component, pageProps, err }: MyAppProps) {
  const router = useRouter();
  const [previousLocation, setPreviousLocation] = useState(``);

  useEffect(() => {
    if (window.location.search) {
      window.location.search
        .replace(`?`, ``)
        .split(`&`)
        .forEach((param) => {
          if (param === ``) return;

          setCookie(null, param, param, cookieParams);
        });
    }

    setCookie(null, `referrer`, document.referrer, cookieParams);
    setCookie(null, `landingPage`, window.location.href, cookieParams);

    router.events.on(`routeChangeStart`, () => NProgress.start());
    router.events.on(`routeChangeError`, () => NProgress.done());
    router.events.on(`routeChangeComplete`, (url) => {
      NProgress.done();

      if (__PROD__) gtm.pageview(url);

      setCookie(null, `referrer`, previousLocation || ``, cookieParams);
      setCookie(null, `conversionPage`, url, cookieParams);
    });
  }, []);

  useEffect(() => {
    setPreviousLocation(router.asPath);
  }, [router.asPath]);

  const asPath = router.asPath.includes(`/index`)
    ? router.asPath.replace(`/index`, ``)
    : router.asPath;

  const pathWithoutQueryParams = asPath.split(`?`)[0];
  const url = urljoin(defaultSeoUrl, pathWithoutQueryParams);

  return (
    <>
      <IntercomProvider
        appId={INTERCOM_APP_ID}
        initializeDelay={INTERCOM_INITIALIZE_DELAY}
      >
        <ApplicationProvider>
          <NavigationProvider>
            <ReakitProvider>
              <Head>
                <meta
                  name="viewport"
                  content="width=device-width, initial-scale=1, shrink-to-fit=no"
                />
                <link rel="canonical" href={url} />

                <script
                  {...jsonLdScriptProps<Organization>({
                    '@context': `https://schema.org`,
                    '@type': `Organization`,
                    url: `https://graphcms.com`,
                    logo: `https://graphcms.com/logo.svg`,
                  })}
                />
              </Head>
              <DefaultSeo
                {...defaultSeo}
                openGraph={{
                  url,
                }}
              />
              {/* Workaround for https://github.com/vercel/next.js/issues/8592 */}
              <Component {...pageProps} err={err} />
            </ReakitProvider>
          </NavigationProvider>
        </ApplicationProvider>
      </IntercomProvider>

      {__PROD__ && <DefaultScripts />}
    </>
  );
}
